import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface DashStat {
  todayTrials: number;
  todayLessons: number;
  weeklyLessons: number;
  monthlyEarning: number;
  pendingEarning: number;
  lastPaymentDate: string | null;
  newStudents: number;
}

interface RecentLesson {
  id: string;
  ders_tarihi: string;
  ders_saati: string | null;
  brans: string | null;
  ders_durumu: string | null;
  ogrenci?: { ogrenci_adi: string };
}

interface RecentTrial {
  id: string;
  planlanan_tarih: string | null;
  planlanan_saat: string | null;
  brans: string | null;
  durum: string | null;
  ogrenci?: { ogrenci_adi: string };
}

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const { ogretmenRecord, profile } = useAuth();
  const [stats, setStats] = useState<DashStat>({ todayTrials: 0, todayLessons: 0, weeklyLessons: 0, monthlyEarning: 0, pendingEarning: 0, lastPaymentDate: null, newStudents: 0 });
  const [todayLessons, setTodayLessons] = useState<RecentLesson[]>([]);
  const [recentTrials, setRecentTrials] = useState<RecentTrial[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ogretmenRecord?.id) { setLoading(false); return; }
    const tid = ogretmenRecord.id;
    const today = new Date().toISOString().split('T')[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
    const monthStart = today.slice(0, 7) + '-01';

    const fetchAll = async () => {
      const [
        { data: todayT }, { data: todayL }, { data: weekL },
        { data: payments }, { data: hakOdemeleri }, { data: trials }, { data: todayLessonsData },
      ] = await Promise.all([
        supabase.from('egtpanel_deneme_dersleri').select('id').eq('ogretmen_id', tid).eq('planlanan_tarih', today),
        supabase.from('egtpanel_aktif_dersler').select('id').eq('ogretmen_id', tid).eq('ders_tarihi', today),
        supabase.from('egtpanel_aktif_dersler').select('id').eq('ogretmen_id', tid).gte('ders_tarihi', weekAgo),
        supabase.from('egtpanel_odemeler').select('ogretmen_payi, tutar').eq('ogretmen_id', tid).eq('yonetici_onay', true).gte('dekont_tarihi', monthStart),
        supabase.from('egtpanel_hak_edis_odemeleri').select('odeme_tutari, odeme_tarihi').eq('ogretmen_id', tid).order('odeme_tarihi', { ascending: false }),
        supabase.from('egtpanel_deneme_dersleri').select('id, planlanan_tarih, planlanan_saat, brans, durum, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', tid).order('planlanan_tarih', { ascending: false }).limit(5),
        supabase.from('egtpanel_aktif_dersler').select('id, ders_tarihi, ders_saati, brans, ders_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi)').eq('ogretmen_id', tid).eq('ders_tarihi', today),
      ]);

      const monthlyEarning = (payments || []).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);
      const totalEarning = (payments || []).reduce((a, p) => a + Number(p.ogretmen_payi || 0), 0);
      const totalPaid = (hakOdemeleri || []).reduce((a, p) => a + Number(p.odeme_tutari), 0);
      const pendingEarning = Math.max(0, totalEarning - totalPaid);
      const lastPaymentDate = hakOdemeleri?.[0]?.odeme_tarihi || null;

      const { count: newStudents } = await supabase.from('egtpanel_yonlendirmeler').select('id', { count: 'exact', head: true }).eq('ogretmen_id', tid).eq('durum', 'Deneme Dersi Planlandı');

      setStats({ todayTrials: todayT?.length || 0, todayLessons: todayL?.length || 0, weeklyLessons: weekL?.length || 0, monthlyEarning, pendingEarning, lastPaymentDate, newStudents: newStudents || 0 });
      setTodayLessons((todayLessonsData || []) as RecentLesson[]);
      setRecentTrials((trials || []) as RecentTrial[]);
      setLoading(false);
    };
    fetchAll();
  }, [ogretmenRecord]);

  const today = new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const firstName = profile?.ad_soyad?.split(' ')[0] || 'Hocam';

  const statCards = [
    { icon: 'ri-user-add-line', label: 'Yönlendirilen Öğrenci', value: stats.newStudents, sub: 'Bekleyen', iconBg: 'bg-blue-50', iconColor: 'text-blue-600' },
    { icon: 'ri-calendar-check-line', label: 'Bugünkü Deneme Dersleri', value: stats.todayTrials, sub: 'Bugün planlandı', iconBg: 'bg-[#f0f4ff]', iconColor: 'text-[#1a2f5a]' },
    { icon: 'ri-book-open-line', label: 'Bugünkü Aktif Derslerim', value: stats.todayLessons, sub: 'Bugünkü program', iconBg: 'bg-purple-50', iconColor: 'text-[#7c6af5]' },
    { icon: 'ri-bar-chart-2-line', label: 'Bu Hafta Ders', value: stats.weeklyLessons, sub: 'Son 7 gün', iconBg: 'bg-indigo-50', iconColor: 'text-indigo-600' },
    { icon: 'ri-money-dollar-circle-line', label: 'Bu Ay Kazancım', value: `₺${stats.monthlyEarning.toLocaleString('tr-TR')}`, sub: 'Onaylı tahsilat payı', iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600' },
    { icon: 'ri-time-line', label: 'Bekleyen Ödeme', value: `₺${stats.pendingEarning.toLocaleString('tr-TR')}`, sub: 'Ödeme bekleniyor', iconBg: 'bg-amber-50', iconColor: 'text-amber-600' },
    { icon: 'ri-calendar-line', label: 'Son Ödeme Tarihi', value: stats.lastPaymentDate || '—', sub: 'Yönetici tarafından', iconBg: 'bg-slate-50', iconColor: 'text-slate-500' },
  ];

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Hoş Geldiniz, {firstName} Hocam!</h1>
          <p className="text-sm text-gray-500 mt-0.5">{today}</p>
        </div>
        <button onClick={() => navigate('/teacher/ders-notlari')}
          className="flex items-center gap-2 bg-[#7c6af5] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#6957e0] transition-colors cursor-pointer whitespace-nowrap w-fit">
          <i className="ri-add-line" /> Ders Notu Ekle
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(card => (
          <div key={card.label} className="bg-white rounded-2xl p-5 border border-gray-100 flex items-start gap-4 hover:-translate-y-0.5 transition-all">
            <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${card.iconBg} flex-shrink-0`}>
              <i className={`${card.icon} text-xl ${card.iconColor}`} />
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 font-medium mb-0.5">{card.label}</p>
              <p className="text-xl font-bold text-gray-800 leading-tight">{card.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{card.sub}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-[#4a2f8a] to-[#7c6af5] rounded-2xl px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <p className="text-white font-semibold text-sm">Bugün {stats.todayLessons} aktif ders planlandı</p>
          <p className="text-white/70 text-xs mt-0.5">
            {todayLessons.length > 0 ? `İlk ders: ${todayLessons[0].ders_saati?.slice(0,5) || ''} — ${(todayLessons[0].ogrenci as {ogrenci_adi:string}|undefined)?.ogrenci_adi || ''} / ${todayLessons[0].brans || ''}` : 'Bugün planlanmış ders yok'}
          </p>
        </div>
        <button onClick={() => navigate('/teacher/aktif-derslerim')}
          className="bg-white/20 border border-white/30 text-white text-sm px-4 py-2 rounded-xl font-semibold hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap">
          Ders Takvimime Git
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center"><i className="ri-book-open-line text-[#7c6af5] text-base" /></div>
              <p className="text-sm font-semibold text-gray-800">Bugünkü Derslerim</p>
              <span className="text-xs bg-[#7c6af5] text-white font-semibold px-2 py-0.5 rounded-full">{todayLessons.length}</span>
            </div>
            <button onClick={() => navigate('/teacher/aktif-derslerim')} className="text-xs text-[#7c6af5] font-semibold hover:underline cursor-pointer whitespace-nowrap">Tümünü Gör</button>
          </div>
          <ul>
            {todayLessons.length === 0 ? (
              <li className="px-5 py-8 text-center text-sm text-gray-400">Bugün planlanmış ders yok</li>
            ) : todayLessons.map((lesson, i) => (
              <li key={lesson.id} className={`flex items-center gap-3 px-5 py-3 ${i < todayLessons.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f5f3ff] flex-shrink-0">
                  <span className="text-[#7c6af5] text-xs font-bold">{lesson.ders_saati?.slice(0,5) || '—'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{(lesson.ogrenci as {ogrenci_adi:string}|undefined)?.ogrenci_adi || '—'}</p>
                  <p className="text-xs text-gray-500">{lesson.brans || '—'}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${lesson.ders_durumu === 'Yapıldı' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  {lesson.ders_durumu || 'Planlandı'}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white rounded-2xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center"><i className="ri-calendar-check-line text-[#1a2f5a] text-base" /></div>
              <p className="text-sm font-semibold text-gray-800">Deneme Derslerim</p>
              <span className="text-xs bg-[#1a2f5a] text-white font-semibold px-2 py-0.5 rounded-full">{recentTrials.length}</span>
            </div>
            <button onClick={() => navigate('/teacher/deneme-derslerim')} className="text-xs text-[#7c6af5] font-semibold hover:underline cursor-pointer whitespace-nowrap">Tümünü Gör</button>
          </div>
          <ul>
            {recentTrials.length === 0 ? (
              <li className="px-5 py-8 text-center text-sm text-gray-400">Deneme dersi yok</li>
            ) : recentTrials.map((trial, i) => (
              <li key={trial.id} className={`flex items-center gap-3 px-5 py-3 ${i < recentTrials.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#1a2f5a] to-[#3b5bdb] flex-shrink-0">
                  <span className="text-white text-xs font-bold">{(trial.ogrenci as {ogrenci_adi:string}|undefined)?.ogrenci_adi?.charAt(0) || '?'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{(trial.ogrenci as {ogrenci_adi:string}|undefined)?.ogrenci_adi || '—'}</p>
                  <p className="text-xs text-gray-500">{trial.brans || '—'} · {trial.planlanan_tarih || '—'}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${trial.durum === 'Yapıldı' ? 'bg-emerald-100 text-emerald-700' : trial.durum === 'Planlandı' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                  {trial.durum || '—'}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}